import React from "react";
import { motion } from "framer-motion";
import { Scissors, TrendingUp, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const BARS = Array.from({ length: 96 }, (_, i) => 0.18 + Math.abs(Math.sin(i * 0.55) * Math.cos(i * 0.23)) * 0.82);
const HOT = [
  { from: 0.12, to: 0.24, score: 94, x: 18 },
  { from: 0.46, to: 0.57, score: 91, x: 50 },
  { from: 0.74, to: 0.86, score: 87, x: 82 },
];
const CLIPS = [
  { words: { es: ["NADIE", "TE", "CUENTA", "ESTO"], en: ["NOBODY", "TELLS", "YOU", "THIS"] }, tag: "TikTok", tint: "from-pink-500/30", img: "https://images.unsplash.com/photo-1673767298248-b128f17f89af?w=500&q=75" },
  { words: { es: ["ASÍ", "LO", "CAMBIÉ", "TODO"], en: ["HOW", "I", "CHANGED", "EVERYTHING"] }, tag: "Reels", tint: "from-yellow-400/30", img: "https://images.unsplash.com/photo-1630797160666-38e8c5ba44c1?w=500&q=75" },
  { words: { es: ["EL", "SECRETO", "ESTÁ", "AQUÍ"], en: ["THE", "SECRET", "IS", "HERE"] }, tag: "Shorts", tint: "from-red-500/30", img: "https://images.unsplash.com/photo-1633933757597-2d09c360c2ad?w=500&q=75" },
];

// Animated "AI cuts a long video into viral clips" visual for the hero.
const SourceStrip = ({ lang, L }) => (
  <div className="relative rounded-2xl border border-white/10 bg-zinc-950/80 backdrop-blur p-4 sm:p-5" data-testid="hero-source-strip">
    <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-3 font-mono">
      <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />podcast_ep42.mp4</span>
      <span>{L("1:24:06 · analizando con IA", "1:24:06 · AI scanning")}</span>
    </div>
    <div className="relative h-20 mt-3 flex items-center gap-[2px] sm:gap-[3px]">
      {BARS.map((h, i) => {
        const hot = HOT.some((s) => i / BARS.length >= s.from && i / BARS.length <= s.to);
        return <span key={i} className={`flex-1 rounded-full ${hot ? "bg-yellow-400" : "bg-zinc-700"}`} style={{ height: `${h * 100}%`, opacity: hot ? 1 : 0.55 }} />;
      })}
      {HOT.map((s, i) => (
        <motion.div key={i} className="absolute top-0 bottom-0 rounded-lg border-2 border-yellow-400/80 bg-yellow-400/10"
          style={{ left: `${s.from * 100}%`, width: `${(s.to - s.from) * 100}%` }}
          initial={{ opacity: 0, scaleY: 0.6 }} animate={{ opacity: 1, scaleY: 1 }} transition={{ delay: 0.6 + i * 0.5, duration: 0.4 }}>
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-black text-zinc-950 shadow-lg">
            <TrendingUp className="h-3 w-3" />{s.score}%
          </span>
          <Scissors className="absolute -bottom-2 -left-2 h-4 w-4 text-yellow-400 rotate-90" />
        </motion.div>
      ))}
      <motion.div className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_18px_4px_rgba(250,204,21,0.7)]"
        animate={{ left: ["0%", "100%"] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} />
    </div>
  </div>
);

const Phone = ({ clip, i, lang, pick }) => (
  <motion.div className="relative w-[26vw] max-w-[11rem] sm:w-44 aspect-[9/16] rounded-[1.6rem] border border-white/15 bg-zinc-900 overflow-hidden shadow-2xl"
    initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: [0, -8, 0] }}
    transition={{ opacity: { delay: 1.4 + i * 0.25 }, y: { delay: 1.4 + i * 0.25, duration: 5 + i, repeat: Infinity, ease: "easeInOut" } }}
    data-testid={`hero-phone-${i}`}>
    <img src={clip.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className={`absolute inset-0 bg-gradient-to-b ${clip.tint} via-transparent to-black/90`} />
    <div className="absolute top-3 inset-x-3 flex items-center justify-between">
      <span className="text-[9px] font-bold uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 rounded-full">{clip.tag}</span>
      <span className="text-[9px] font-black text-yellow-400">{HOT[i].score}%</span>
    </div>
    <div className="absolute bottom-10 inset-x-2 flex flex-wrap justify-center gap-x-1 gap-y-0.5 text-center">
      {pick(clip.words).map((w, j) => (
        <motion.span key={j} className="font-black text-[10px] sm:text-sm leading-tight"
          style={{ WebkitTextStroke: "0.6px #000", textShadow: "0 2px 6px rgba(0,0,0,.9)" }}
          animate={{ color: ["#ffffff", "#FACC15", "#ffffff"], scale: [1, 1.15, 1] }}
          transition={{ duration: 0.6, delay: 2 + i * 0.3 + j * 0.45, repeat: Infinity, repeatDelay: pick(clip.words).length * 0.45 }}>
          {w}
        </motion.span>
      ))}
    </div>
    <div className="absolute bottom-3 inset-x-4 h-1 rounded-full bg-white/15 overflow-hidden">
      <motion.div className="h-full bg-yellow-400" animate={{ width: ["0%", "100%"] }} transition={{ duration: 6 + i, repeat: Infinity, ease: "linear" }} />
    </div>
  </motion.div>
);

export const HeroVisual = () => {
  const { lang, L, pick } = useLang();
  const l = lang === "en" ? "en" : "es";
  return (
    <div className="relative max-w-4xl mx-auto px-5 mt-14" data-testid="hero-visual">
      <div className="absolute -inset-x-10 top-10 h-64 bg-yellow-400/10 blur-3xl rounded-full pointer-events-none" />
      <SourceStrip lang={l} L={L} />
      <svg className="w-full h-16 sm:h-20 -my-1" viewBox="0 0 100 20" preserveAspectRatio="none">
        {HOT.map((s, i) => (
          <motion.path key={i} d={`M ${(s.from + s.to) * 50} 0 C ${(s.from + s.to) * 50} 10, ${s.x} 10, ${s.x} 20`}
            fill="none" stroke="#FACC15" strokeWidth="0.35" strokeDasharray="1.2 1"
            initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.8 }} transition={{ delay: 1 + i * 0.4, duration: 0.8 }} />
        ))}
      </svg>
      <div className="grid grid-cols-3 place-items-center">
        {CLIPS.map((c, i) => <Phone key={i} clip={c} i={i} lang={l} pick={pick} />)}
      </div>
      <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-zinc-500">
        <Sparkles className="h-3.5 w-3.5 text-yellow-400" />
        {L("1 vídeo largo → 3 clips virales con subtítulos, en segundos", "1 long video → 3 viral clips with captions, in seconds")}
      </p>
    </div>
  );
};
